/**
 * The STONEFALL logo (04 §7): letters of chunky blocks shaded like the game's boulders (white
 * highlight, grey stone, orange shade), outlined in black with a blue drop shadow. On the title the
 * letters fall into place one by one and a glint sweeps across them now and then.
 */

import { makeCanvas } from '../../render/canvas';
import { BLACK, BLUE, DARK_GREY, GREY, LIGHT_GREY, ORANGE, rgb, WHITE, YELLOW } from '../../render/palette';
import type { Screen } from '../../render/screen';
import { hash } from '../../render/sprites/types';
import { LOGO_ROWS, logoGlyph } from './logoFont';

export interface LogoLetter {
  /** Left edge of the letter's blocks within the logo. */
  readonly x: number;
  readonly art: HTMLCanvasElement;
  readonly glint: HTMLCanvasElement;
}

export interface LogoArt {
  readonly w: number;
  readonly h: number;
  readonly letters: readonly LogoLetter[];
}

/** Room around a letter's blocks: one pixel of outline, two of drop shadow. */
const PAD = 1;
const EXTRA = 3;

type Mask = (x: number, y: number) => boolean;

/** The palette colour of pixel (x, y) of a letter whose blocks cover `inside`, or -1 for none. Pure. */
export function logoPixel(inside: Mask, x: number, y: number, h: number): number {
  if (inside(x, y)) {
    if (!inside(x, y - 1)) return WHITE;
    if (!inside(x, y + 1) || !inside(x, y + 2)) return ORANGE;
    if (!inside(x - 1, y) || !inside(x - 2, y)) return LIGHT_GREY;
    if (!inside(x + 1, y) || !inside(x + 2, y)) return ORANGE;
    const band = y / h;
    const speck = hash(x >> 1, y, 5) % 13 === 0;
    if (band < 0.3 || (band < 0.38 && ((x >> 1) + y) % 2 === 0)) return speck ? GREY : LIGHT_GREY;
    return speck ? DARK_GREY : GREY;
  }
  for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) if (inside(x + dx, y + dy)) return BLACK;
  if (inside(x - 2, y - 2)) return BLUE;
  return -1;
}

function paint(w: number, h: number, colourAt: (x: number, y: number) => number): HTMLCanvasElement {
  const canvas = makeCanvas(w, h);
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;
  const img = ctx.createImageData(w, h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const c = colourAt(x, y);
      if (c < 0) continue;
      const [r, g, b] = rgb(c);
      img.data.set([r, g, b, 255], (y * w + x) * 4);
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

/** Build the logo for `text` with blocks `bw` x `bh` pixels and `gap` pixels between letters. */
export function buildLogo(text: string, bw = 4, bh = 6, gap = 6): LogoArt {
  const letters: LogoLetter[] = [];
  let x = 0;
  const h = LOGO_ROWS * bh;
  for (const ch of text) {
    const rows = logoGlyph(ch);
    const cols = Math.max(...rows.map((r) => r.length));
    const w = cols * bw;
    if (ch !== ' ') {
      const inside: Mask = (px, py) => {
        if (px < 0 || py < 0 || px >= w || py >= h) return false;
        return rows[Math.floor(py / bh)]?.[Math.floor(px / bw)] === '#';
      };
      const cw = w + PAD + EXTRA;
      const chh = h + PAD + EXTRA;
      const art = paint(cw, chh, (px, py) => logoPixel(inside, px - PAD, py - PAD, h));
      const glint = paint(cw, chh, (px, py) => {
        const c = logoPixel(inside, px - PAD, py - PAD, h);
        return c === ORANGE ? YELLOW : c === BLACK || c === BLUE || c < 0 ? -1 : WHITE;
      });
      letters.push({ x, art, glint });
    }
    x += w + gap;
  }
  return { w: Math.max(0, x - gap), h, letters };
}

const built = new Map<string, LogoArt>();

/** The logo for `text`, built once per page load and shared by every screen that shows it. */
export function logoFor(text: string): LogoArt {
  let art = built.get(text);
  if (!art) {
    art = buildLogo(text);
    built.set(text, art);
  }
  return art;
}

const FALL_MS = 380;
const BOUNCE_MS = 170;
const STAGGER_MS = 90;

/** When letter `i` of `n` starts falling: a scattered order, like rocks coming loose. Pure. */
export function dropStart(i: number, n: number): number {
  return ((i * 4) % Math.max(1, n)) * STAGGER_MS;
}

/** Letter `i`'s vertical offset `ms` after the drop began: falling, a small bounce, then still. Pure. */
export function dropOffset(ms: number, i: number, n: number): number {
  const t = ms - dropStart(i, n);
  if (t < 0) return -90;
  if (t < FALL_MS) return Math.round(-90 * (1 - (t / FALL_MS) ** 2));
  const b = t - FALL_MS;
  if (b < BOUNCE_MS) return -Math.round(5 * Math.sin((Math.PI * b) / BOUNCE_MS));
  return 0;
}

/** All letters have landed. */
export function dropDone(ms: number, n: number): boolean {
  let last = 0;
  for (let i = 0; i < n; i++) last = Math.max(last, dropStart(i, n));
  return ms >= last + FALL_MS + BOUNCE_MS + 260;
}

const GLINT_EVERY = 5200;
const GLINT_MS = 1100;

/**
 * Draw the logo with its blocks' top-left at (x, y). `dropMs` is the time since the letters began
 * to fall (null: already in place); `ms` drives the glint.
 */
export function drawLogo(screen: Screen, art: LogoArt, x: number, y: number, ms: number, dropMs: number | null): void {
  const n = art.letters.length;
  const t = ms % GLINT_EVERY;
  const g = t < GLINT_MS ? -30 + ((art.w + 60) * t) / GLINT_MS : null;
  art.letters.forEach((letter, i) => {
    const dy = dropMs === null ? 0 : dropOffset(dropMs, i, n);
    const lx = x + letter.x - PAD;
    const ly = y + dy - PAD;
    screen.image(letter.art, lx, ly);
    if (dropMs !== null) drawDust(screen, dropMs - dropStart(i, n) - FALL_MS, lx, y + art.h, letter.art.width);
    if (g === null || dy !== 0) return;
    // The glint: a band leaning right, cut from the white copy two pixels at a time.
    for (let c = 0; c < letter.glint.width; c += 2) {
      const gx = letter.x + c;
      const y0 = Math.max(0, Math.floor((g - 4 - gx) * 2));
      const y1 = Math.min(letter.glint.height, Math.ceil((g + 4 - gx) * 2));
      if (y1 > y0) screen.ctx.drawImage(letter.glint, c, y0, 2, y1 - y0, lx + c, ly + y0, 2, y1 - y0);
    }
  });
}

/** A puff of grit either side of a letter that has just landed. */
function drawDust(screen: Screen, since: number, x: number, floor: number, w: number): void {
  if (since < 0 || since > 260) return;
  const spread = Math.round(since / 26);
  const rise = Math.round(Math.sin((since / 260) * Math.PI) * 4);
  const colour = since < 130 ? LIGHT_GREY : GREY;
  for (const k of [0, 1, 2]) {
    screen.fill(x - 2 - spread - k * 3, floor - rise - k, 2, 1, colour);
    screen.fill(x + w + spread + k * 3 - 2, floor - rise - k, 2, 1, colour);
  }
}
